import { useAuth } from "@clerk/nextjs";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import path from "constants/path";
import useFilters from "features/FilterDrawer/hooks/useFilters";
import {
  convertDurationToDays,
  getQueryFromFilters,
} from "features/FilterDrawer/utils/queryParams/filterQueryParams";
import { THREADS_QUERY_KEY } from "features/Sidebar/hooks/useThreads";
import { parseThreadResponse } from "features/Thread/types";
import useCredits from "features/User/hooks/useCredits";
import useIncognitoMode from "features/User/hooks/useIncognitoMode";
import useUserSettings from "features/User/hooks/useUserSettings";
import { createThread, Paper, SearchMode, ThreadResponse } from "helpers/api";
import { truncateWithEllipsis } from "helpers/format";
import { threadPath } from "helpers/pageUrl";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useLabels from "hooks/useLabels";
import useSearchMode from "hooks/useSearchMode";
import { useAppDispatch } from "hooks/useStore";
import { getThreadQueryKey } from "hooks/useThread";
import { useRouter } from "next/router";
import { toast } from "react-toastify";
import { setIsSearching } from "store/slices/search";
import { useNewThreadEvents } from "../useNewThreadEvents";

export const CREATE_THREAD_MUTATION_KEY = ["createThread"] as const;

// Regex pattern to match the loading page route: /search/[title]/new (with optional trailing slash)
const LOADING_PAGE_PATTERN = new RegExp(`^${path.SEARCH}/[^/]+${path.NEW}/?$`);

// URL length constants
const TITLE_MAX_LENGTH = 50; // Keep title short for clean URLs
// Limit query to prevent URL length issues, but keep it generous
// Most browsers support ~2000 chars total, leaving room for base URL and encoding
const QUERY_MAX_LENGTH = 1500;

// Mutation key for thread creation - export for use in other components

// Helper function to generate loading page URL
const getLoadingPageUrl = (userMessage: string): string => {
  const truncatedTitle = truncateWithEllipsis(userMessage, TITLE_MAX_LENGTH);
  // Truncate very long queries to prevent URL length issues
  // 1500 chars is enough for most use cases and retry logic
  const safeQuery =
    userMessage.length > QUERY_MAX_LENGTH
      ? truncateWithEllipsis(userMessage, QUERY_MAX_LENGTH)
      : userMessage;

  return `${path.SEARCH}/${encodeURIComponent(truncatedTitle)}${
    path.NEW
  }?q=${encodeURIComponent(safeQuery)}`;
};

/**
 * `useNewThread` is a custom React hook for creating a new thread.
 * It uses `react-query` for managing API calls and caching, and integrates with Next.js routing for navigation.
 *
 * When creating a thread, first we check if the user is logged in. If not, we redirect them through sign-in to /search/new, which will again use this hook to try to create the thread.
 *
 * If the user is logged in, we create the thread, save the resulting thread in the react-query cache, and redirect to the new thread's page.
 */
export function useNewThread() {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { isLoaded } = useAuth();

  const { currentSearchMode, changeSearchMode } = useSearchMode();
  const { appliedFilters } = useFilters();

  const { lang } = useUserSettings();

  const dispatch = useAppDispatch();

  const { isIncognitoModeActive } = useIncognitoMode();
  const { trackNewThread } = useNewThreadEvents();
  const { spendCreditOnSearch } = useCredits();
  const [failedToCreateThread] = useLabels(
    "features.threads.failed-to-create-thread"
  );

  // Mutation to create a new thread - focused only on API call and react-query operations
  const mutation = useMutation(
    async ({
      userMessage,
      searchMode,
      sources,
    }: {
      userMessage: string;
      searchMode: SearchMode;
      sources?: Paper[];
    }) => {
      // API only accepts "day" units for duration, so convert it before passing.
      const filters = convertDurationToDays(appliedFilters);

      const threadResponse = await createThread({
        userMessage,
        isProEnabled: currentSearchMode === SearchMode.PRO_RESEARCH,
        isIncognito: isIncognitoModeActive,
        filters: getQueryFromFilters(filters),
        lang,
        searchMode,
        sources: sources?.map((s) => s.paper_id),
      });

      // Spend credit on the search
      const searchId = threadResponse.interactions[0].search_id;
      if (searchId) {
        spendCreditOnSearch(searchId, searchMode);
      }

      // If the user just executed a pro research search, go back to pro analysis mode
      if (currentSearchMode === SearchMode.PRO_RESEARCH) {
        setTimeout(() => {
          changeSearchMode(SearchMode.PRO_ANALYSIS);
        }, 2000);
      }

      return threadResponse;
    },
    {
      mutationKey: CREATE_THREAD_MUTATION_KEY,
      retry: false, // Disable retries to prevent multiple promise rejections
      onSuccess: async (threadResponse: ThreadResponse) => {
        // Cache the new thread data for the created thread so that we don't refetch it after redirecting to the thread page.
        queryClient.setQueryData(
          getThreadQueryKey(threadResponse.thread_id),
          parseThreadResponse(threadResponse)
        );

        // Update list of threads in sidebar to reflect new thread
        queryClient.invalidateQueries({
          queryKey: THREADS_QUERY_KEY,
          exact: true,
        });
      },
    }
  );

  const createNewThread = async ({
    userMessage,
    searchMode,
    actionSource,
    sources,
    onSuccessCallback,
  }: {
    userMessage: string;
    searchMode: SearchMode;
    actionSource: MixpanelActionSource;
    sources?: Paper[];
    onSuccessCallback?: () => void;
  }) => {
    if (!isLoaded) return;
    trackNewThread(userMessage, actionSource);

    // Set loading state and navigate to loading page
    dispatch(setIsSearching(true));

    // Wait for navigation to complete before starting thread creation
    // This prevents the race condition where thread completes before reaching loading page
    await router.push(getLoadingPageUrl(userMessage));

    try {
      // Execute the mutation with fresh router state available
      const threadResponse = await mutation.mutateAsync({
        userMessage,
        searchMode,
        sources,
      });

      // Check if user is still on the loading page before redirecting
      // Need to use window.location as router.asPath can be stale
      const currentPath = window.location.pathname;
      const isStillOnLoadingPage = LOADING_PAGE_PATTERN.test(currentPath);

      if (isStillOnLoadingPage) {
        // Handle success routing here with fresh router state
        let redirect = threadPath(
          threadResponse.thread_id,
          threadResponse.title
        );

        // Add language parameter to URL if it exists
        if (lang) {
          redirect += `?lang=${encodeURIComponent(lang)}`;
        }

        // Use router.replace since we have fresh state
        router.replace(redirect, undefined, { shallow: true });
      }

      onSuccessCallback?.();
    } catch (error) {
      // Check if user is still on the loading page before redirecting
      // Need to use window.location as router.asPath can be stale
      const currentPath = window.location.pathname;
      const isStillOnLoadingPage = LOADING_PAGE_PATTERN.test(currentPath);

      if (isStillOnLoadingPage) {
        // Handle error routing here with fresh router state
        // For the home page URL, we can use the full query as well
        const homeUrlWithQuery = `${path.HOME}?q=${encodeURIComponent(
          userMessage
        )}`;

        router.replace(homeUrlWithQuery);
        // Show error toast
        toast.error(failedToCreateThread);
      }
    } finally {
      // Always clear loading state
      dispatch(setIsSearching(false));
    }
  };

  return {
    isCreating: mutation.isLoading,
    error: mutation.error as Error | null,
    createNewThread,
  };
}
